import React, {useCallback, useState} from 'react';
import { FormSearch } from 'grommet-icons';
import { TextInput } from 'grommet';
import {useMapsLibrary} from '@vis.gl/react-google-maps';
import {useAutocompleteSuggestions} from './useAutoCompleteSuggestions';

interface Props {
  onPlaceSelect: (place: google.maps.places.Place | null) => void;
  defaultValue?: string;
}

export const AutocompleteCustom = ({onPlaceSelect, defaultValue}: Props) => {
  const places = useMapsLibrary('places');

  const [inputValue, setInputValue] = useState<string>(defaultValue || '');
  const [selectedValue, setSelectedValue] = useState(defaultValue || '');
  const {suggestions, resetSession} = useAutocompleteSuggestions(inputValue === selectedValue ? '' : inputValue);

  const handleInput = useCallback((value: string) => {
    setInputValue(value);
    setSelectedValue('');
    onPlaceSelect(null);
  }, [onPlaceSelect]);

  const handleSuggestionClick = useCallback(
    async (suggestion: google.maps.places.AutocompleteSuggestion) => {
      if (!places) return;
      if (!suggestion.placePrediction) return;

      const place = suggestion.placePrediction.toPlace();

      await place.fetchFields({
        fields: [
          'location',
          'addressComponents',
          'formattedAddress',
        ]
      });

      const label = suggestion.placePrediction.text.text;
      setInputValue(label);
      setSelectedValue(label);

      // calling fetchFields invalidates the session-token, so we now have to call
      // resetSession() so a new one gets created for further search
      resetSession();

      onPlaceSelect(place);
    },
    [places, onPlaceSelect, resetSession]
  );

  return (
    <TextInput
      suggestions={suggestions.map(suggestion => ({
        label: suggestion.placePrediction?.text.text || '',
        value: suggestion
      }))}
      onSuggestionSelect={({ suggestion }) => handleSuggestionClick(
        (suggestion as { value: google.maps.places.AutocompleteSuggestion }).value
      )}
      value={inputValue}
      onChange={event => handleInput(event.target.value)}
      icon={<FormSearch />}
      size="small"
      placeholder="Search for a city or address"
      aria-label="Search for a city or address"
      dropProps={{ background: 'white', elevation: 'medium' }}
      style={{ width: '100%' }}
    />
  );
};
